import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Text from './Text';

export const Box = styled.button`
  flex-shrink: 0;
  user-select: none;
  display: flex;
  padding: 6px;
  padding-left: 12px;
  border-radius: 4px;
  gap: 8px;
  border: solid 1px ${({ theme }) => theme.colors.lightGray};
  align-items: center;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

interface SwitchesProps {
  $inactive?: boolean;
  $active?: boolean;
  $disabled: boolean;
}

export const Switches = styled.div<SwitchesProps>`
  display: flex;
  position: relative;
  gap: 4px;
  &:after {
    content: '';
    width: 33px;
    height: 24px;
    border-radius: 4px;
    position: absolute;
    left: 0;
    top: 0;
    transition: all ease-out 0.2s;
  }
  ${({ $inactive }) =>
    $inactive &&
    css`
      &:after {
        background-color: ${({ theme }) => theme.colors.lightGray};
      }
    `}
  ${({ $active }) =>
    $active &&
    css`
      color: ${({ theme }) => theme.colors.white};
      &:after {
        left: 37px;
        background-color: ${({ theme }) => theme.colors.purple};
      }
    `}
  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      cursor: not-allowed;

      ${Switch} {
        color: ${theme.colors.gray};
      }

      &:after {
        background-color: ${theme.colors.lightGray};
      }
    `}
`;

export const Switch = styled(Text)<{ $active?: boolean }>`
  width: 33px;
  height: 24px;
  justify-content: center;
  align-items: center;
  display: flex;
  color: ${({ theme }) => theme.colors.gray};
  position: relative;
  transition: color ease-out 0.2s;
  span {
    position: relative;
    z-index: 1;
  }
  ${({ $active }) =>
    $active &&
    css`
      color: ${({ theme }) => theme.colors.white};
    `}
`;

interface Props {
  text: ReactNode;
  isActive: boolean;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  offLabel?: string;
  onLabel?: string;
}

const FancySwitch: FC<Props> = ({
  text,
  isActive,
  onClick,
  className,
  disabled = false,
  onLabel = 'On',
  offLabel = 'Off',
}) => {
  return (
    <Box
      onClick={onClick}
      className={className}
      type="button"
      disabled={disabled}
    >
      <Text $type="smallBold" as="div">
        {text}
      </Text>
      <Switches $inactive={!isActive} $active={isActive} $disabled={disabled}>
        <Switch $type="smallHeadline" as="div">
          <span>{offLabel}</span>
        </Switch>
        <Switch $active={isActive} $type="smallHeadline" as="div">
          <span>{onLabel}</span>
        </Switch>
      </Switches>
    </Box>
  );
};

export default FancySwitch;
